import { useFormContext } from "react-hook-form";
import API from "@zillow/mortgage-api-client";
import { useLastValidValues } from "../../utils/form/useLastValidValues.esm.js";
import { DEFAULT_PARTNER_ID } from "../../utils/mortgagehelpers/constants.esm.js";
import { castAsNumberIfNotNan, getIsAmountNanError, COMMON_PERCENT_CONFIG, COMMON_AMOUNT_CONFIG } from "../../utils/zhl-calculator.esm.js";
const DISCOUNT_POINTS = {
  defaultPoint: "defaultPoint",
  oneDiscountPoint: "oneDiscountPoint",
  twoDiscountPoints: "twoDiscountPoints"
};
const NON_REQUIRED_COMMON_AMOUNT_CONFIG = {
  validate: {
    nan: getIsAmountNanError("Must be numeric amount.")
  },
  setValueAs: castAsNumberIfNotNan
};
const FieldConfig = {
  homePrice: {
    name: "homePrice",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  downPayment: {
    name: "downPayment",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  downPaymentPercent: {
    name: "downPaymentPercent",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  interestRate: {
    name: "interestRate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  purchasingAnyDiscountPoints: {
    name: "purchasingAnyDiscountPoints",
    options: [{
      value: DISCOUNT_POINTS.defaultPoint,
      label: "No"
    }, {
      value: DISCOUNT_POINTS.oneDiscountPoint,
      label: "Yes, 1 point"
    }, {
      value: DISCOUNT_POINTS.twoDiscountPoints,
      label: "Yes, 2 points"
    }],
    config: {
      required: "Required"
    }
  },
  loanOriginationFee: {
    name: "loanOriginationFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  discountPoints: {
    name: "discountPoints",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  creditReportFee: {
    name: "creditReportFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  titleSearchFee: {
    name: "titleSearchFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  titleInsurance: {
    name: "titleInsurance",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  attorneyFees: {
    name: "attorneyFees",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  settelementOrEscrowFee: {
    name: "settelementOrEscrowFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  homeownerInsurance: {
    name: "homeownerInsurance",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  propertyTaxes: {
    name: "propertyTaxes",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  prepaidInterest: {
    name: "prepaidInterest",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  appraisalFee: {
    name: "appraisalFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  homeInspectionFee: {
    name: "homeInspectionFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  pestInspectionFee: {
    name: "pestInspectionFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  surveyFee: {
    name: "surveyFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  recordingFee: {
    name: "recordingFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  courierFee: {
    name: "courierFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  notaryFee: {
    name: "notaryFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  hoaTransferFee: {
    name: "hoaTransferFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  floodCertificationFee: {
    name: "floodCertificationFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  bankProcessingFee: {
    name: "bankProcessingFee",
    config: {
      ...NON_REQUIRED_COMMON_AMOUNT_CONFIG
    }
  },
  propertyTaxRate: {
    name: "propertyTaxRate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  }
};
const validateAndTrackOptionsBlock = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true
};
function isSameFloat(float1, float2) {
  const nextVal = typeof float1 === "string" ? Number(float1) : float1;
  const lastVal = typeof float2 === "string" ? Number(float2) : float2;
  return Math.abs(Number(nextVal == null ? void 0 : nextVal.toFixed(2)) - Number(lastVal == null ? void 0 : lastVal.toFixed(2))) < Number.EPSILON;
}
const getZillowURL = (path) => {
  const zillowOrigin = "https://www.zillow.com";
  return `${zillowOrigin}/${path.replace(/^\//, "")}`;
};
function useLastValidClosingCostCalculator() {
  const form = useFormContext();
  return useLastValidValues(form, getClosingCostValues);
}
const fetchRate = async (homePrice, downPayment) => {
  const rate = await fetchInterestRate({
    homePrice,
    downPayment
  });
  return rate == 0 ? standardRate.Fixed30Year : rate;
};
const getZeroIfFieldEmpty = (field) => {
  const fieldVal = typeof field === "string" ? Number(field) : field;
  return !fieldVal ? 0 : fieldVal;
};
const roundToTheNearestTenth = (val) => {
  return parseFloat(val.toFixed(1));
};
const roundToNearestInteger = (val) => {
  return Math.round(val);
};
function getClosingCostValues(lastValidFields) {
  const {
    homePrice,
    loanOriginationFee,
    creditReportFee,
    discountPoints,
    titleSearchFee,
    titleInsurance,
    attorneyFees,
    settelementOrEscrowFee,
    homeownerInsurance,
    propertyTaxes,
    prepaidInterest,
    appraisalFee,
    homeInspectionFee,
    pestInspectionFee,
    surveyFee,
    recordingFee,
    courierFee,
    notaryFee,
    hoaTransferFee,
    floodCertificationFee,
    bankProcessingFee
  } = lastValidFields;
  const closingCost = getZeroIfFieldEmpty(loanOriginationFee) + getZeroIfFieldEmpty(creditReportFee) + getZeroIfFieldEmpty(discountPoints) + getZeroIfFieldEmpty(titleSearchFee) + getZeroIfFieldEmpty(titleInsurance) + getZeroIfFieldEmpty(attorneyFees) + getZeroIfFieldEmpty(settelementOrEscrowFee) + getZeroIfFieldEmpty(homeownerInsurance) + getZeroIfFieldEmpty(propertyTaxes) + getZeroIfFieldEmpty(prepaidInterest) + getZeroIfFieldEmpty(appraisalFee) + getZeroIfFieldEmpty(homeInspectionFee) + getZeroIfFieldEmpty(pestInspectionFee) + getZeroIfFieldEmpty(surveyFee) + getZeroIfFieldEmpty(recordingFee) + getZeroIfFieldEmpty(courierFee) + getZeroIfFieldEmpty(notaryFee) + getZeroIfFieldEmpty(hoaTransferFee) + getZeroIfFieldEmpty(floodCertificationFee) + getZeroIfFieldEmpty(bankProcessingFee);
  const calculations = {
    closingCost,
    closingCostPercentage: Number((closingCost / homePrice * 100).toFixed(1))
  };
  const values = {
    fields: lastValidFields,
    calculations
  };
  return values;
}
const standardRate = {
  Fixed30Year: 4.176
};
const LOAN_TO_VALUE_RATE_BUCKET = {
  NORMAL: "Normal",
  HIGH: "High",
  VERY_HIGH: "VeryHigh"
};
const LOAN_AMOUNT_RATE_BUCKET = {
  MICRO: "Micro",
  SMALL_CONFORMING: "SmallConforming",
  CONFORMING: "Conforming",
  UNSURE: "Unsure"
};
const RATE_TYPE = {
  STANDARD: "Standard",
  NON_STANDARD: "Non-standard",
  INVALID: "Invalid"
};
function getLoanToValueRateBucket(homePrice, downPayment) {
  if (typeof homePrice === "undefined" || typeof downPayment === "undefined") {
    return LOAN_TO_VALUE_RATE_BUCKET.NORMAL;
  }
  const downPercent = downPayment / homePrice * 100;
  if (downPercent < 5) {
    return LOAN_TO_VALUE_RATE_BUCKET.VERY_HIGH;
  } else if (downPercent >= 5 && downPercent < 20) {
    return LOAN_TO_VALUE_RATE_BUCKET.HIGH;
  }
  return LOAN_TO_VALUE_RATE_BUCKET.NORMAL;
}
function getLoanAmountRateBucket(homePrice, downPayment) {
  if (typeof homePrice === "undefined" || typeof downPayment === "undefined") {
    return LOAN_AMOUNT_RATE_BUCKET.UNSURE;
  }
  const remainder = homePrice - downPayment;
  if (remainder < 1e5) {
    return LOAN_AMOUNT_RATE_BUCKET.MICRO;
  } else if (remainder >= 1e5 && remainder < 2e5) {
    return LOAN_AMOUNT_RATE_BUCKET.SMALL_CONFORMING;
  } else if (remainder >= 2e5 && remainder < 453100) {
    return LOAN_AMOUNT_RATE_BUCKET.CONFORMING;
  }
  return LOAN_AMOUNT_RATE_BUCKET.UNSURE;
}
function getCreditScoreBucket(creditScore) {
  const buckets = {
    R_760_: "VeryHigh",
    R_740_759: "VeryHigh",
    R_720_739: "High",
    R_700_719: "High",
    R_680_699: "High",
    R_660_679: "Low",
    R_640_659: "Low",
    R_620_639: "Low",
    R_600_619: "Low",
    R_560_599: "Low",
    R_300_559: "Low"
  };
  return creditScore && buckets[creditScore];
}
function calculateRateBuckets({
  homePrice,
  downPayment,
  loanProgram,
  creditScore
}) {
  const loanToValueRateBucket = getLoanToValueRateBucket(homePrice, downPayment);
  const loanAmountRateBucket = getLoanAmountRateBucket(homePrice, downPayment);
  const creditScoreBucket = getCreditScoreBucket(creditScore);
  const result = {
    rateType: RATE_TYPE.STANDARD,
    loanProgram,
    loanAmountBucket: loanAmountRateBucket,
    loanToValueBucket: loanToValueRateBucket,
    creditScoreBucket
  };
  if (result.loanAmountBucket !== LOAN_AMOUNT_RATE_BUCKET.CONFORMING || result.loanToValueBucket !== LOAN_TO_VALUE_RATE_BUCKET.NORMAL || result.creditScoreBucket !== "VeryHigh") {
    result.rateType = RATE_TYPE.NON_STANDARD;
  }
  if (homePrice - downPayment < 0) {
    result.rateType = RATE_TYPE.INVALID;
  }
  return result;
}
function getRateQuery({
  loanAmount: loanAmountParam,
  homePrice,
  downPayment,
  loanProgram = "Fixed30Year",
  creditScore = "R_720_739",
  va = false,
  refinance = false,
  fha = false,
  jumbo = false
}) {
  const rateBucketResults = calculateRateBuckets({
    homePrice,
    downPayment,
    loanProgram,
    creditScore
  });
  const rateQuery = {
    stateAbbreviation: API.stateAbbreviation,
    program: rateBucketResults.loanProgram
  };
  if (rateBucketResults.loanAmountBucket !== LOAN_AMOUNT_RATE_BUCKET.UNSURE) {
    rateQuery.loanAmountBucket = rateBucketResults.loanAmountBucket;
    rateQuery.loanToValueBucket = rateBucketResults.loanToValueBucket;
  } else {
    if (homePrice) {
      const loanAmount = loanAmountParam || homePrice - downPayment;
      rateQuery.propertyBucket = {
        propertyValue: homePrice,
        loanAmount,
        location: {
          stateAbbreviation: API.stateAbbreviation
        }
      };
    }
  }
  if (va) rateQuery.loanType = "VA";
  if (rateBucketResults.creditScoreBucket) rateQuery.creditScoreBucket = rateBucketResults.creditScoreBucket;
  if (refinance) rateQuery.refinance = true;
  if (fha) rateQuery.loanType = "FHA";
  if (jumbo) rateQuery.loanType = "Jumbo";
  return rateQuery;
}
function getStandardInterestRate(loanProgram = "Fixed30Year") {
  return standardRate[loanProgram];
}
const getCurrentRates = async (inputs) => {
  if (!API || !API.MAX || !API.MAX.GET) {
    return Promise.resolve({
      rates: {
        rateQuery: {
          rate: 0
        }
      }
    });
  }
  try {
    return await API.MAX.GET({
      action: "getCurrentRates",
      timeout: 3e3,
      input: inputs
    });
  } catch (error) {
    return {
      rates: {
        rateQuery: {
          rate: 0
        }
      }
    };
  }
};
const fetchInterestRate = (rateQueryParams) => {
  const {
    loanProgram
  } = rateQueryParams;
  const standardRate2 = getStandardInterestRate(loanProgram);
  return getCurrentRates({
    partnerId: API.partnerId ? API.partnerId : DEFAULT_PARTNER_ID,
    queries: {
      rateQuery: getRateQuery(rateQueryParams)
    }
  }).then((response) => {
    const newRate = response.rates.rateQuery.rate;
    if (newRate === 0) {
      return standardRate2;
    } else {
      return newRate;
    }
  });
};
const getTaxRate = async (apiInput) => {
  if (!API || !API.MAX || !API.MAX.GET) {
    return Promise.resolve({
      rate: 0
    });
  }
  try {
    return await API.MAX.GET({
      action: "getAnnualPropertyTaxRate",
      timeout: 3e3,
      input: apiInput
    });
  } catch (error) {
    return {
      rate: 0
    };
  }
};
const isValidZipcode = (value) => {
  const regex = /^\d{5}$/;
  return regex.test(value);
};
const fetchPropertyTaxRate = async () => {
  const apiInput = {
    partnerId: API.partnerId ? API.partnerId : DEFAULT_PARTNER_ID
  };
  const zipcode = API.zipcode;
  if (zipcode && isValidZipcode(zipcode) && zipcode.length === 5) {
    apiInput["zipCode"] = zipcode;
  } else {
    apiInput["stateAbbreviation"] = API.stateAbbreviation;
  }
  return getTaxRate(apiInput).then((response) => {
    const newRate = response.rate;
    return newRate;
  });
};
export {
  DISCOUNT_POINTS,
  FieldConfig,
  LOAN_AMOUNT_RATE_BUCKET,
  LOAN_TO_VALUE_RATE_BUCKET,
  NON_REQUIRED_COMMON_AMOUNT_CONFIG,
  RATE_TYPE,
  calculateRateBuckets,
  fetchInterestRate,
  fetchPropertyTaxRate,
  fetchRate,
  getCurrentRates,
  getLoanAmountRateBucket,
  getLoanToValueRateBucket,
  getRateQuery,
  getStandardInterestRate,
  getTaxRate,
  getZillowURL,
  isSameFloat,
  roundToNearestInteger,
  roundToTheNearestTenth,
  standardRate,
  useLastValidClosingCostCalculator,
  validateAndTrackOptionsBlock
};
